import type { BlockEditFormState } from '@ContractBuilder/modules/block-edit';
import type { Node } from '@tiptap/pm/model';
import type { Selection, SelectionRange, Transaction } from '@tiptap/pm/state';
import { TableMap } from '@tiptap/pm/tables';
import type { ContentNodeWithPos } from 'prosemirror-utils';

import type { BorderSettings, SelectionRect } from './types';

// assuming that there is a given node above
export const findFirstNodeAbove = (
  nodeName: string,
  { $from }: Selection,
): { node: Node; before: number; depth: number } | null => {
  for (let depth = $from.depth; depth >= 0; depth--) {
    const node = $from.node(depth);
    if (node.type.name === nodeName) {
      return { node, before: $from.before(depth), depth };
    }
  }
  return null;
};

export const selectionCoversTextblock = ({ $from, $to }: SelectionRange, textblock: Node, pos: number): boolean =>
  $from.pos <= pos + 1 && $to.pos >= pos + 1 + textblock.content.size;

// Tiptap's removeEmptyTextStyle strips the mark across every non-textblock node the selection
// touches, so inside a list it wipes the styles of every item; this one stays inside the selection.
export const removeEmptyTextStyle = (tr: Transaction, selection: Selection) => {
  const textStyle = tr.doc.type.schema.marks.textStyle;
  selection.ranges.forEach(({ $from, $to }) => {
    tr.doc.nodesBetween($from.pos, $to.pos, (node, pos) => {
      const style = node.marks.find((mark) => mark.type === textStyle);
      if (node.isText && style && !Object.values(style.attrs).some(Boolean)) {
        tr.removeMark(Math.max(pos, $from.pos), Math.min(pos + node.nodeSize, $to.pos), textStyle);
      }
    });
  });
};

export const setCellBorders =
  (tr: Transaction) =>
  (node: Node, posInTable: number, { isTop, isBottom, isLeft, isRight }: BorderSettings) => {
    const attrs = {
      ...node.attrs,
      ...(isTop && { hidetop: isTop }),
      ...(isBottom && { hidebottom: isBottom }),
      ...(isLeft && { hideleft: isLeft }),
      ...(isRight && { hideright: isRight }),
    };

    const newNode = node.type.create(attrs, node.content, node.marks);
    tr.setNodeMarkup(posInTable, newNode.type, newNode.attrs, newNode.marks);
  };

export const setAdjacentBorders =
  (
    setBorders: (node: Node, posInTable: number, borders: BorderSettings) => void,
    table: ContentNodeWithPos,
    rect: SelectionRect,
  ) =>
  (direction: 'left' | 'top') => {
    let adjacentRect: SelectionRect = rect;

    if (direction === 'left') {
      adjacentRect = { ...rect, right: rect.left, left: rect.left - 1 };
    } else if (direction === 'top') {
      adjacentRect = { ...rect, bottom: rect.top, top: rect.top - 1 };
    }
    const tableMap = TableMap.get(table.node);
    const cellPositions = tableMap.cellsInRect(adjacentRect);

    cellPositions.forEach((cellPos: number) => {
      const cellNode = table.node.nodeAt(cellPos);
      if (!cellNode) return;
      const posInTable = table.start + cellPos;
      if (direction === 'left') {
        setBorders(cellNode, posInTable, { isRight: true });
      } else if (direction === 'top') {
        setBorders(cellNode, posInTable, { isBottom: true });
      }
    });
  };

export const getCurrentContentId = (block?: BlockEditFormState, currentVariationIdx?: number) =>
  currentVariationIdx ? block?.variations?.[currentVariationIdx]?.id : (block?.id ?? 'create');
